import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { functions } from "../firebase";

type HistoryView = "messages" | "threads";

type ThreadRow = {
  id: string;
  type: string;
  title: string;
  threadScope?: string;
  originModule?: string;
  reviewCategory?: string;
  excludeFromPrincipalReviewDefault?: boolean;
  ptuCampaignId?: string;
  audienceFamilyId?: string | null;
  familyIds?: string[];
  schoolYear: string | null;
  createdBy: string | null;
  createdAt: string | null;
  lastMessageAt: string | null;
  lastMessageText: string;
  participantIds: string[];
  participantLabels: string[];
  studentKeys: string[];
  studentNames: string[];
};

type ThreadDetail = {
  id: string;
  type: string;
  title: string;
  clubId: string | null;
  classId: string | null;
  threadScope?: string;
  originModule?: string;
  reviewCategory?: string;
  excludeFromPrincipalReviewDefault?: boolean;
  ptuCampaignId?: string;
  audienceFamilyId?: string | null;
  familyIds?: string[];
  schoolYear: string | null;
  createdBy: string | null;
  createdAt: string | null;
  lastMessageAt: string | null;
  lastMessageText: string;
  participantIds: string[];
  participantLabels: Array<{ id: string; label: string }>;
  studentKeys: string[];
};

type MessageRow = {
  id: string;
  threadId: string;
  messageId: string;
  threadType: string;
  threadTitle: string;
  threadScope?: string;
  originModule?: string;
  reviewCategory?: string;
  excludeFromPrincipalReviewDefault?: boolean;
  ptuCampaignId?: string;
  audienceFamilyId?: string;
  familyIds?: string[];
  schoolYear: string | null;
  senderId: string;
  senderDisplayName: string;
  textPreview: string;
  attachmentCount: number;
  attachmentNames: string[];
  participantLabels: string[];
  studentNames: string[];
  createdAt: string | null;
};

type ThreadMessageRow = {
  id: string;
  senderId: string;
  senderDisplayName: string;
  text: string;
  createdAt: string | null;
  attachments: Array<{
    id?: string;
    name?: string;
    downloadUrl?: string;
    contentType?: string;
    size?: number;
  }>;
};

function formatDateTime(v?: string | null) {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return v;
  }
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoIsoDate(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function messagePreview(v: string, max = 160) {
  const t = String(v || "");
  return t.length > max ? `${t.slice(0, max)}...` : t;
}

function safeAttachmentName(a: any) {
  return a?.name || a?.id || "Attachment";
}

function joinOrDash(values: string[]) {
  const clean = Array.from(new Set((values || []).map((v) => String(v || "").trim()).filter(Boolean)));
  return clean.length ? clean.join(", ") : "-";
}

function csvCell(value: unknown) {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

function exportMessagesCsv(rows: MessageRow[]) {
  const headers = [
    "Date",
    "Sender",
    "Thread",
    "Type",
    "School Year",
    "Participants",
    "Students",
    "Message Preview",
    "Attachments",
    "Thread ID",
    "Message ID",
  ];
  const lines = rows.map((row) => [
    formatDateTime(row.createdAt),
    row.senderDisplayName || row.senderId,
    row.threadTitle,
    row.threadType,
    row.schoolYear || "",
    joinOrDash(row.participantLabels),
    joinOrDash(row.studentNames),
    row.textPreview,
    row.attachmentCount ? `${row.attachmentCount}: ${joinOrDash(row.attachmentNames)}` : "",
    row.threadId,
    row.messageId,
  ]);
  const csv = [headers, ...lines].map((line) => line.map(csvCell).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `message-history-${todayIsoDate()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function MessagingHistoryPage() {
  const [view, setView] = useState<HistoryView>("messages");

  const [messageRows, setMessageRows] = useState<MessageRow[]>([]);
  const [threadRows, setThreadRows] = useState<ThreadRow[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [ptuVisibility, setPtuVisibility] = useState<"exclude" | "include" | "only">("exclude");
  const [yearFilter, setYearFilter] = useState("all");
  const [senderFilter, setSenderFilter] = useState("");
  const [startDate, setStartDate] = useState(daysAgoIsoDate(30));
  const [endDate, setEndDate] = useState(todayIsoDate());

  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [thread, setThread] = useState<ThreadDetail | null>(null);
  const [threadMessages, setThreadMessages] = useState<ThreadMessageRow[]>([]);

  async function loadMessages() {
    setLoading(true);
    setErr(null);

    try {
      const fn = httpsCallable(functions, "listReviewableMessages");
      const res: any = await fn({
        search,
        typeFilter,
        ptuVisibility,
        yearFilter,
        senderFilter,
        startDate,
        endDate,
        limit: 300,
        scanLimit: 1500,
      });

      setMessageRows(Array.isArray(res?.data?.rows) ? res.data.rows : []);
      setYears(Array.isArray(res?.data?.availableYears) ? res.data.availableYears : []);
    } catch (e: any) {
      setErr(e?.message || "Failed to load message history.");
      setMessageRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadThreads() {
    setLoading(true);
    setErr(null);

    try {
      const fn = httpsCallable(functions, "listReviewableThreads");
      const res: any = await fn({
        search,
        typeFilter,
        ptuVisibility,
        yearFilter,
        limit: 250,
      });

      setThreadRows(Array.isArray(res?.data?.rows) ? res.data.rows : []);
      setYears(Array.isArray(res?.data?.availableYears) ? res.data.availableYears : []);
    } catch (e: any) {
      setErr(e?.message || "Failed to load thread history.");
      setThreadRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadCurrentView() {
    if (view === "messages") await loadMessages();
    else await loadThreads();
  }

  async function openThread(threadId: string, messageId?: string) {
    setSelectedThreadId(threadId);
    setSelectedMessageId(messageId || null);
    setDetailLoading(true);
    setErr(null);

    try {
      const fn = httpsCallable(functions, "getReviewableThreadDetail");
      const res: any = await fn({ threadId, maxMessages: 500 });

      setThread((res?.data?.thread ?? null) as ThreadDetail | null);
      setThreadMessages(Array.isArray(res?.data?.messages) ? res.data.messages : []);
    } catch (e: any) {
      setErr(e?.message || "Failed to load thread detail.");
      setThread(null);
      setThreadMessages([]);
    } finally {
      setDetailLoading(false);
    }
  }

  async function backfillNextBatch() {
    setBackfillBusy(true);
    setErr(null);
    setBackfillStatus("Starting backfill batch...");

    try {
      const fn = httpsCallable(functions, "backfillMessageHistoryIndex");
      let cursor = "";
      let totalThreads = 0;
      let totalMessages = 0;
      let totalIndexed = 0;

      for (let i = 0; i < 20; i += 1) {
        const res: any = await fn({
          startAfterThreadId: cursor || undefined,
          maxThreads: 50,
          maxMessagesPerThread: 1000,
        });
        const data = res?.data || {};
        totalThreads += Number(data.processedThreads || 0);
        totalMessages += Number(data.processedMessages || 0);
        totalIndexed += Number(data.indexedMessages || 0);
        cursor = String(data.lastThreadId || "");
        setBackfillStatus(`Indexed ${totalIndexed} of ${totalMessages} messages across ${totalThreads} threads...`);
        if (!data.hasMore || !cursor) break;
      }

      setBackfillStatus(`Backfill complete. Indexed ${totalIndexed} of ${totalMessages} messages across ${totalThreads} threads.`);
      await loadMessages();
    } catch (e: any) {
      setErr(e?.message || "Failed to backfill message history index.");
    } finally {
      setBackfillBusy(false);
    }
  }

  useEffect(() => {
    loadCurrentView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const selectedSummary = useMemo(
    () => threadRows.find((r) => r.id === selectedThreadId) || null,
    [threadRows, selectedThreadId]
  );

  return (
    <div className="card">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <h2 style={{ marginTop: 0 }}>Messaging History</h2>
          <p className="muted" style={{ marginTop: 6 }}>
            Message-level review for app conversations, announcements, and recycled threads.
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button className="btn secondary" onClick={backfillNextBatch} disabled={backfillBusy}>
            {backfillBusy ? "Backfilling..." : "Backfill Index"}
          </button>
          <button className="btn secondary" onClick={loadCurrentView} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button className="btn" onClick={() => exportMessagesCsv(messageRows)} disabled={!messageRows.length}>
            Export CSV
          </button>
        </div>
      </div>

      {err ? <div className="error">{err}</div> : null}
      {backfillStatus ? <div className="success">{backfillStatus}</div> : null}

      <div className="segmented-control" style={{ marginTop: 14 }}>
        <button type="button" className={view === "messages" ? "active" : ""} onClick={() => setView("messages")}>
          Recent Messages
        </button>
        <button type="button" className={view === "threads" ? "active" : ""} onClick={() => setView("threads")}>
          Threads
        </button>
      </div>

      <div className="grid4" style={{ marginTop: 12 }}>
        <div>
          <label className="label">Search</label>
          <input
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="message, sender, participant, student, thread..."
          />
        </div>

        <div>
          <label className="label">Sender</label>
          <input
            className="input"
            value={senderFilter}
            onChange={(e) => setSenderFilter(e.target.value)}
            placeholder="sender name or uid"
          />
        </div>

        <div>
          <label className="label">Type</label>
          <select className="input" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">all</option>
            <option value="ptu">PTU</option>
            <option value="direct">direct</option>
            <option value="broadcast">broadcast</option>
            <option value="club">club</option>
            <option value="staffDirect">staff direct</option>
            <option value="staffGroup">staff group</option>
            <option value="staffAnnouncement">staff announcement</option>
          </select>
        </div>

        <div>
          <label className="label">PTU Visibility</label>
          <select className="input" value={ptuVisibility} onChange={(e) => setPtuVisibility(e.target.value as any)}>
            <option value="exclude">Exclude PTU by default</option>
            <option value="include">Include PTU</option>
            <option value="only">Only PTU</option>
          </select>
        </div>

        <div>
          <label className="label">School Year</label>
          <select className="input" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
            <option value="all">all</option>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Start Date</label>
          <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>

        <div>
          <label className="label">End Date</label>
          <input className="input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>

        <div style={{ display: "flex", alignItems: "end", gap: 8 }}>
          <button className="btn" onClick={loadCurrentView} disabled={loading}>
            {loading ? "Searching..." : "Apply Filters"}
          </button>
        </div>
      </div>

      <hr className="hr" />

      <div style={{ display: "grid", gridTemplateColumns: "minmax(420px, 0.9fr) minmax(520px, 1.1fr)", gap: 16, alignItems: "start" }}>
        <div className="card messaging-compose-panel" style={{ maxHeight: "75vh", overflow: "auto" }}>
          <h3 style={{ marginTop: 0 }}>{view === "messages" ? "Recent Messages" : "Threads"}</h3>
          <div className="muted" style={{ marginBottom: 10 }}>
            {view === "messages"
              ? `${messageRows.length} indexed message${messageRows.length === 1 ? "" : "s"}`
              : `${threadRows.length} thread${threadRows.length === 1 ? "" : "s"}`}
          </div>

          {loading ? (
            <div className="muted">Loading...</div>
          ) : view === "messages" ? (
            messageRows.length === 0 ? (
              <div className="muted">No matching indexed messages found. Run Backfill Index if older messages are missing.</div>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {messageRows.map((m) => {
                  const selected = m.threadId === selectedThreadId && m.messageId === selectedMessageId;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => openThread(m.threadId, m.messageId)}
                      className={`messaging-choice ${selected ? "selected" : ""}`}
                      style={{ textAlign: "left", padding: 12, cursor: "pointer" }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "start" }}>
                        <div style={{ fontWeight: 900 }}>{m.senderDisplayName || m.senderId || "Unknown sender"}</div>
                        <span className="pill">{m.threadType}</span>
                        {m.reviewCategory === "ptu" || m.threadScope === "ptu" || m.originModule === "ptu" ? <span className="pill">PTU</span> : null}
                      </div>
                      <div className="muted" style={{ marginTop: 5, fontSize: 12 }}>
                        {formatDateTime(m.createdAt)} / {m.threadTitle || m.threadId}
                      </div>
                      <div style={{ marginTop: 8, lineHeight: 1.45 }}>
                        {messagePreview(m.textPreview || (m.attachmentCount ? "(attachment only)" : "(no text)"))}
                      </div>
                      <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>
                        Participants: {joinOrDash(m.participantLabels)}
                      </div>
                      {m.studentNames.length ? (
                        <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                          Students: {joinOrDash(m.studentNames)}
                        </div>
                      ) : null}
                      {m.attachmentCount ? (
                        <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                          Attachments: {m.attachmentCount} {joinOrDash(m.attachmentNames)}
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )
          ) : threadRows.length === 0 ? (
            <div className="muted">No matching threads found.</div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {threadRows.map((r) => {
                const selected = r.id === selectedThreadId && !selectedMessageId;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => openThread(r.id)}
                    className={`messaging-choice ${selected ? "selected" : ""}`}
                    style={{ textAlign: "left", padding: 12, cursor: "pointer" }}
                  >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ fontWeight: 800 }}>{r.title || "(untitled thread)"}</div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <span className="pill">{r.type}</span>
                        {r.reviewCategory === "ptu" || r.threadScope === "ptu" || r.originModule === "ptu" ? <span className="pill">PTU</span> : null}
                      </div>
                    </div>
                    <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                      {r.schoolYear || "-"} / {formatDateTime(r.lastMessageAt)}
                    </div>
                    <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
                      Participants: {joinOrDash(r.participantLabels)}
                    </div>
                    {r.studentNames.length ? (
                      <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                        Students: {joinOrDash(r.studentNames)}
                      </div>
                    ) : null}
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                      {messagePreview(r.lastMessageText || "(no message preview)")}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="card" style={{ padding: 16, minHeight: 400 }}>
          <h3 style={{ marginTop: 0 }}>Context</h3>

          {!selectedThreadId ? (
            <div className="muted">Select a message or thread to review context.</div>
          ) : detailLoading ? (
            <div className="muted">Loading context...</div>
          ) : !thread ? (
            <div className="muted">Unable to load thread detail.</div>
          ) : (
            <>
              <div style={{ display: "grid", gap: 6 }}>
                <div><b>Title:</b> {thread.title || "-"}</div>
                <div><b>Thread ID:</b> <code>{thread.id}</code></div>
                <div><b>Type:</b> {thread.type}</div>
                {thread.reviewCategory === "ptu" || thread.threadScope === "ptu" || thread.originModule === "ptu" ? (
                  <>
                    <div><b>Review Category:</b> PTU</div>
                    <div><b>PTU Campaign:</b> {thread.ptuCampaignId || "-"}</div>
                    <div><b>Audience Family:</b> {thread.audienceFamilyId || "-"}</div>
                    <div><b>Family IDs:</b> {thread.familyIds?.join(", ") || "-"}</div>
                    <div><b>Default Principal Review:</b> {thread.excludeFromPrincipalReviewDefault ? "Excluded" : "Included"}</div>
                  </>
                ) : null}
                <div><b>School Year:</b> {thread.schoolYear || "-"}</div>
                <div><b>Created By:</b> {thread.createdBy || "-"}</div>
                <div><b>Created At:</b> {formatDateTime(thread.createdAt)}</div>
                <div><b>Last Message At:</b> {formatDateTime(thread.lastMessageAt)}</div>
                <div><b>Participants:</b> {thread.participantLabels.map((p) => p.label).join(", ") || "-"}</div>
                <div><b>Student Keys:</b> {thread.studentKeys.join(", ") || "-"}</div>
              </div>

              <hr className="hr" />

              <h4>Messages</h4>
              {threadMessages.length === 0 ? (
                <div className="muted">No messages found.</div>
              ) : (
                <div style={{ display: "grid", gap: 10, maxHeight: "58vh", overflow: "auto" }}>
                  {threadMessages.map((m) => {
                    const selected = m.id === selectedMessageId;
                    return (
                      <div
                        key={m.id}
                        className={`message-history-detail ${selected ? "selected" : ""}`}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                          <div style={{ fontWeight: 800 }}>
                            {m.senderDisplayName || m.senderId || "Unknown sender"}
                          </div>
                          <div className="muted" style={{ fontSize: 12 }}>
                            {formatDateTime(m.createdAt)}
                          </div>
                        </div>
                        <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
                          Sender ID: {m.senderId || "-"} / Message ID: {m.id}
                        </div>
                        <div style={{ marginTop: 10, whiteSpace: "pre-wrap" }}>
                          {m.text || <span className="muted">(no text)</span>}
                        </div>
                        {m.attachments?.length ? (
                          <div style={{ marginTop: 10 }}>
                            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Attachments</div>
                            <div style={{ display: "grid", gap: 6 }}>
                              {m.attachments.map((a, idx) => (
                                <div key={`${m.id}-${idx}`} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                  <span className="pill">{a.contentType || "file"}</span>
                                  {a.downloadUrl ? (
                                    <a href={a.downloadUrl} target="_blank" rel="noreferrer" className="btn secondary">
                                      {safeAttachmentName(a)}
                                    </a>
                                  ) : (
                                    <span>{safeAttachmentName(a)}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selectedSummary ? (
        <>
          <hr className="hr" />
          <details>
            <summary>Selected thread summary JSON</summary>
            <pre style={{ whiteSpace: "pre-wrap" }}>
{JSON.stringify(selectedSummary, null, 2)}
            </pre>
          </details>
        </>
      ) : null}
    </div>
  );
}
